import { useState } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSafety = () => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    setTimeout(() => {
      const el = document.getElementById('safety');
      if (el) {
        const offset = 72;
        const top = el.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    }, 50);
  };

  const handleSubscribe = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setSubmitted(true);
      setEmail('');
      return;
    }

    formData.delete('phone_alt');

    if (!email.trim() || !email.includes('@')) return;

    setSubmitting(true);
    try {
      const response = await fetch('https://readdy.ai/api/form/d8vu73gbhec1he3opfm0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as any).toString(),
      });
      if (response.ok) {
        setSubmitted(true);
        setEmail('');
      }
    } catch {
      // silently fail
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer className="w-full pt-16 md:pt-20 pb-8 px-6 md:px-10">
      <div className="max-w-6xl mx-auto">
        <div className="bg-foreground-950 rounded-3xl p-8 md:p-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12">
            <div>
              <h3 className="text-2xl md:text-3xl font-heading font-semibold text-background-50 mb-3">
                Stay Updated
              </h3>
              <p className="text-sm text-background-50/60 leading-relaxed mb-5">
                Get the latest guides and tips for using the <strong className="text-background-50/80">itaobuyspreadsheet</strong> system delivered straight to your inbox.
              </p>

              {submitted ? (
                <div className="flex items-center gap-2 text-sm text-accent-400">
                  <i className="ri-check-line text-lg"></i>
                  Thanks for subscribing! Check your inbox soon.
                </div>
              ) : (
                <form
                  data-readdy-form
                  onSubmit={handleSubscribe}
                  className="relative"
                >
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    className="anti-spam-field"
                    value=""
                    readOnly
                  />
                  <div className="flex items-center gap-2">
                    <input
                      type="email"
                      name="email"
                      placeholder="your@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="flex-1 bg-transparent border-b border-background-50/30 text-sm text-background-50 placeholder:text-background-50/40 py-2 px-1 focus:outline-none focus:border-background-50/60"
                    />
                    <button
                      type="submit"
                      disabled={submitting}
                      className="inline-flex items-center px-4 py-2 text-sm font-medium text-foreground-950 bg-background-50 rounded-full hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
                    >
                      {submitting ? '...' : (
                        <>
                          Subscribe
                          <i className="ri-arrow-right-line ml-1.5"></i>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>

            <div>
              <h4 className="text-sm font-semibold text-background-50 uppercase tracking-wider mb-4">Quick Links</h4>
              <nav className="flex flex-col gap-2.5">
                <Link
                  to="/"
                  onClick={scrollToTop}
                  className="text-sm text-background-50/60 hover:text-background-50 transition-colors whitespace-nowrap underline decoration-background-50/20 hover:decoration-background-50/50"
                >
                  Home
                </Link>
                <Link
                  to="/what-is-itaobuyspreadsheet"
                  className="text-sm text-background-50/60 hover:text-background-50 transition-colors whitespace-nowrap underline decoration-background-50/20 hover:decoration-background-50/50"
                >
                  Guides &amp; Resources
                </Link>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-sm text-background-50/60 hover:text-background-50 transition-colors whitespace-nowrap underline decoration-background-50/20 hover:decoration-background-50/50"
                >
                  Main Platform
                </a>
                <Link
                  to="/"
                  onClick={scrollToSafety}
                  className="text-sm text-background-50/60 hover:text-background-50 transition-colors whitespace-nowrap underline decoration-background-50/20 hover:decoration-background-50/50"
                >
                  Safety Information
                </Link>
              </nav>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-background-50 uppercase tracking-wider mb-4">Categories</h4>
              <nav className="grid grid-cols-2 gap-2">
                {categories.slice(0, 8).map((cat) => (
                  <a
                    key={cat.seq}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-xs text-background-50/60 hover:text-background-50 transition-colors whitespace-nowrap"
                  >
                    {cat.name}
                  </a>
                ))}
              </nav>
            </div>
          </div>

          <div className="mt-10 pt-8 border-t border-background-50/15 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-5">
              <a href="https://x.com" target="_blank" rel="noopener nofollow" className="text-sm text-background-50/50 hover:text-background-50 transition-colors whitespace-nowrap underline decoration-background-50/15">
                <i className="ri-twitter-x-line mr-1"></i> X
              </a>
              <a href="https://linkedin.com" target="_blank" rel="noopener nofollow" className="text-sm text-background-50/50 hover:text-background-50 transition-colors whitespace-nowrap underline decoration-background-50/15">
                <i className="ri-linkedin-line mr-1"></i> LinkedIn
              </a>
            </div>
            <p className="text-xs text-background-50/40 text-center">
              &copy; {new Date().getFullYear()} itaobuy.help — Your guide to smarter China shopping.
            </p>
            <div className="flex items-center gap-4">
              <a href="#" className="text-xs text-background-50/50 hover:text-background-50 transition-colors whitespace-nowrap underline decoration-background-50/15">
                Privacy Policy
              </a>
              <a href="#" className="text-xs text-background-50/50 hover:text-background-50 transition-colors whitespace-nowrap underline decoration-background-50/15">
                Terms
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}